import { HttpClient, HttpContext } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { BYPASS_AUTH } from "@dcc/vestas-online/auth/data-access";
import { withCache } from "@ngneat/cashew";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

const STORAGE_KEY = "vestas_online_maintenance_mode";

export interface MaintenanceMode {
    maintenance: boolean;
    message: string;
}

@Injectable({ providedIn: "root" })
export class MaintenanceApiService {
    private readonly httpClient = inject(HttpClient);

    /**
     * Save maintenance mode flag to sessionStorage.
     * Stores '1' for true and '0' for false. Silent noop on errors (e.g. SSR).
     */
    setMaintenanceModeSession(value: boolean): void {
        sessionStorage.setItem(STORAGE_KEY, value ? "1" : "0");
    }

    /**
     * Read maintenance mode flag from sessionStorage.
     * Returns false when no value is set or on any error.
     */
    getMaintenanceModeSession(): boolean {
        try {
            const v = sessionStorage.getItem(STORAGE_KEY);
            return v === "1";
        } catch {
            return false;
        }
    }

    /**
     * Remove maintenance mode flag from sessionStorage. Silent noop on errors.
     */
    clearMaintenanceModeSession(): void {
        sessionStorage.removeItem(STORAGE_KEY);
    }

    getMaintenanceMode(applicationId: string): Observable<MaintenanceMode | null> {
        return this.httpClient
            .get<MaintenanceMode[]>(this.maintenanceModeUrl(applicationId))
            .pipe(map((messages) => (messages.length > 0 ? messages[0] : null)));
    }

    getPlatformMaintenanceMode(): Observable<MaintenanceMode | null> {
        return this.httpClient
            .get<MaintenanceMode[]>(this.platformMaintenanceModeUrl(), {
                context: withCache({
                    ttl: 60_000,
                    context: new HttpContext().set(BYPASS_AUTH, true)
                })
            })
            .pipe(map((messages) => (messages.length > 0 ? messages[0] : null)));
    }

    private maintenanceModeUrl(applicationId: string): string {
        return `api/platform/messages/maintenance/${applicationId}`;
    }

    private platformMaintenanceModeUrl(): string {
        return "api/platform/messages/maintenance/platform";
    }
}
